import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styles from './BlogEjsPage.module.css';
import { LanguageContext } from '../../Component/LanguageContext/LanguageContext';
import useScrollToTopWithDelay from '../../hooks/useScrollToTopWithDelay';
import { FaHeart, FaShareAlt, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import axios from 'axios';

const marketingBackendUrl = import.meta.env.VITE_MARKETING_BACKEND_URL;
const BlogEjsPage = () => {
  useScrollToTopWithDelay(500);
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { direction } = useContext(LanguageContext);
  const isRTL = direction === 'rtl';
  const navigate = useNavigate();
  const [wishlistStatus, setWishlistStatus] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const [notification, setNotification] = useState({ show: false, message: '' });

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await fetch(`${marketingBackendUrl}/maflam/blogs/${id}`);
        const data = await response.json();
        setBlog(data.data);
        setLoading(false);

      } catch (error) {
        console.error('Error fetching blog:', error);
        setError('Failed to fetch blog');
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id]);

  useEffect(() => {
    const checkWishlistStatus = async () => {
      try {
        const userEmail = localStorage.getItem("useremailidis");
        if (!userEmail || !blog?._id) return;

        const response = await axios.get(`${marketingBackendUrl}/maflam/blog-wishlist/${userEmail}`);
        
        if (response.data.success) {
          const wishlistBlogs = response.data.data;
          const isInWishlist = wishlistBlogs.some(
            wishlistBlog => wishlistBlog.headingEnglish === blog.heading
          );
          setWishlistStatus(isInWishlist);
        }
      } catch (error) {
        console.error('Error checking wishlist status:', error);
      }
    };

    if (blog) {
      checkWishlistStatus();
    }
  }, [blog]);

  const handleWishlistToggle = async () => {
    try {
      const userEmail = localStorage.getItem("useremailidis");
      
      if (!userEmail) {
        navigate("/home/login");
        return;
      }

      if (wishlistStatus) {
        const response = await axios.post(`${marketingBackendUrl}/maflam/blog-wishlist/remove-blog`, {
          userEmail,
          headingEnglish: blog.heading
        });

        if (response.data.success) {
          setWishlistStatus(false);
          setNotification({
            show: true,
            message: direction === "rtl" 
              ? 'تمت إزالة المقال من قائمة الرغبات بنجاح'
              : 'Blog removed from wishlist successfully'
          });
          setTimeout(() => {
            setNotification({ show: false, message: '' });
          }, 2000);
        }
      } else {
        const blogDetails = {
          headingEnglish: blog.heading,
          headingArabic: blog.heading,
          subheadingEnglish: blog.subheading,
          subheadingArabic: blog.subheading,
          thumbnail: blog.bannerImage,
          redirectionLink: `/home/blogDetails/${blog._id}`
        };

        const response = await axios.post(`${marketingBackendUrl}/maflam/blog-wishlist`, {
          userEmail,
          blogDetails
        });

        if (response.data.success) {
          setWishlistStatus(true);
          setNotification({
            show: true,
            message: direction === "rtl" 
              ? 'تمت إضافة المقال إلى قائمة الرغبات بنجاح'
              : 'Blog added to wishlist successfully'
          });
          setTimeout(() => {
            setNotification({ show: false, message: '' });
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
      if (error.response?.status === 400) {
        setNotification({
          show: true,
          message: direction === "rtl"
            ? 'المقال موجود بالفعل في قائمة الرغبات'
            : 'Blog already exists in wishlist'
        });
      }
    }
  };

  const handleShare = () => {
    setShowSharePopup(true);
  };

  const copyToClipboard = () => {
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setNotification({
        show: true,
        message: isRTL ? 'تم نسخ الرابط إلى الحافظة!' : 'Link copied to clipboard!'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '' });
      }, 2000);
      setShowSharePopup(false);
    }).catch((error) => console.error('Error copying link:', error));
  };

  const handleGoBack = () => {
    navigate(-1); // Go back to the previous page
  };

  if (loading) return <div className={styles.blogContainer}>Loading...</div>;
  if (error) return <div className={styles.blogContainer}>Error: {error}</div>;
  if (!blog) return <div className={styles.blogContainer}>Blog not found</div>;

  const formatDate = (date) => {
    const dateObj = new Date(date);
    
    // Format the day in English
    const day = dateObj.toLocaleDateString('en-US', { day: 'numeric' });
    
    // Format the month in Arabic
    const month = dateObj.toLocaleDateString('ar-EG', { month: 'long' });
    
    // Format the year in English
    const year = dateObj.getFullYear();

    return `${day} ${month} ${year}`;
  };

  const renderContent = (contentItem, index) => {
    // Skip rendering if content is just "na"
    if (contentItem.value === "na" || 
        (Array.isArray(contentItem.value) && contentItem.value.every(item => item === "na"))) {
      return null;
    }

    const processText = (text) => {
      // Skip processing if text is "na"
      if (text === "na") return null;

      const parts = [];
      let currentText = text;
      let lastIndex = 0;

      // Process both link and bold patterns
      while (true) {
        const linkMatch = /itisalink\("([^"]+)"\)havingurl\("([^"]+)"\)/g.exec(currentText);
        const boldMatch = /itisbold\("([^"]+)"\)/g.exec(currentText);

        // If no more matches found, break
        if (!linkMatch && !boldMatch) {
          if (lastIndex < currentText.length) {
            parts.push(currentText.slice(lastIndex));
          }
          break;
        }

        // Find which pattern comes first
        const linkIndex = linkMatch ? linkMatch.index : Infinity;
        const boldIndex = boldMatch ? boldMatch.index : Infinity;
        
        if (linkIndex < boldIndex) {
          // Process link
          if (linkIndex > lastIndex) {
            parts.push(currentText.slice(lastIndex, linkIndex));
          }
          
          parts.push(
            <a 
              key={`link-${linkIndex}`}
              href={linkMatch[2]}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: 'rgb(57, 255, 251)',
                textDecoration: 'none',
                transition: 'opacity 0.2s ease',
              }}
              onMouseOver={(e) => e.target.style.opacity = '0.8'}
              onMouseOut={(e) => e.target.style.opacity = '1'}
            >
              {linkMatch[1]}
            </a>
          );
          
          lastIndex = linkIndex + linkMatch[0].length;
          currentText = currentText.slice(lastIndex);
          lastIndex = 0;
        } else {
          // Process bold
          if (boldIndex > lastIndex) {
            parts.push(currentText.slice(lastIndex, boldIndex));
          }
          
          parts.push(
            <strong 
              key={`bold-${boldIndex}`}
              style={{ 
                fontWeight: '700'
              }}
            >
              {boldMatch[1]}
            </strong>
          );
          
          lastIndex = boldIndex + boldMatch[0].length;
          currentText = currentText.slice(lastIndex);
          lastIndex = 0;
        }
      }

      return parts.length > 0 ? parts : text;
    };

    switch (contentItem.type) {
      case 'heading':
        return contentItem.value === "na" ? null : (
          <h2 key={index} className={styles.contentHeading}>
            {processText(contentItem.value)}
          </h2>
        );

      case 'paragraph':
        return contentItem.value === "na" ? null : (
          <p key={index} className={styles.contentParagraph}>
            {processText(contentItem.value)}
          </p>
        );

      case 'numbered-list':
        // Filter out "na" items from the list
        const filteredItems = contentItem.value.filter(item => item !== "na");
        return filteredItems.length === 0 ? null : (
          <ol key={index} className={styles.numberedList}>
            {filteredItems.map((item, listIndex) => (
              <li key={listIndex}>{processText(item)}</li>
            ))}
          </ol>
        );

      case 'image':
        return contentItem.value === "na" ? null : (
          <div key={index} className={styles.imageWrapper}>
            <img
              src={contentItem.value}
              alt={`Blog content ${index + 1}`}
              className={styles.contentImage}
              loading="lazy"
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {isLoading && (
        <div className={styles.loaderWrapper}>
          <span className={styles.loader}></span>
        </div>
      )}
      <div style={{ direction: isRTL ? 'rtl' : 'ltr' }} className={styles.blogContainer}>
        {notification.show && (
          <div className={styles.notification}>
            {notification.message}
          </div>
        )}
        <button style={{position: 'absolute', top: '100px', right: '1rem' , zIndex: '1000' , background:'rgb(57, 255, 251)' , color:'black' , border:'none' , borderRadius:'1.5rem' , padding:'0.5rem 1rem' , cursor:'pointer'}} className={styles.goBackButton} onClick={handleGoBack}>
          {isRTL ? <FaArrowRight style={{ marginLeft: '0.5rem' }} /> : <FaArrowLeft style={{ marginRight: '0.5rem' }} />}
          {isRTL ? 'عودة' : 'Go Back'}
        </button>
        <div className={styles.bannerSection}>
          <img 
            src={blog.bannerImage} 
            alt={blog.heading !== "na" ? blog.heading : ""} 
            className={styles.bannerImage}
          />
          <div className={styles.headerContent}>
            {blog.heading !== "na" && <h1 className={styles.heading}>{blog.heading}</h1>}
            
            
          </div>
        </div>

        <div style={{position: 'relative'}} className={styles.content}>
          <div className={styles.metaInfo}>
            {blog.instructor !== "na" && (
              <span className={styles.instructor}>{blog.instructor}</span>
            )}
            {blog.writingTime !== "na" && (
              <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' , marginBottom: '1.5rem' }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17316C0.00433284 8.00043 -0.1937 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8078C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C20 8.68678 19.7413 7.38642 19.2388 6.17316C18.7362 4.95991 17.9997 3.85752 17.0711 2.92893C16.1425 2.00035 15.0401 1.26375 13.8268 0.761205C12.6136 0.258658 11.3132 0 10 0ZM10 18C8.41775 18 6.87103 17.5308 5.55544 16.6518C4.23985 15.7727 3.21447 14.5233 2.60897 13.0615C2.00347 11.5997 1.84504 9.99112 2.15372 8.43928C2.4624 6.88743 3.22433 5.46197 4.34315 4.34314C5.46197 3.22433 6.88743 2.4624 8.43928 2.15372C9.99113 1.84504 11.5997 2.00346 13.0615 2.60896C14.5233 3.21446 15.7727 4.23984 16.6518 5.55544C17.5308 6.87103 18 8.41775 18 10C18 12.1217 17.1571 14.1566 15.6569 15.6569C14.1566 17.1571 12.1217 18 10 18Z" fill="#39FFFB" />
                  <path d="M14.0858 6.31374L9.24264 11.1569L7.12132 9.03557C6.93378 8.84803 6.67943 8.74268 6.41421 8.74268C6.149 8.74268 5.89464 8.84803 5.70711 9.03557C5.51957 9.22311 5.41421 9.47746 5.41421 9.74268C5.41421 10.0079 5.51957 10.2622 5.70711 10.4498L8.53553 13.2782C8.72307 13.4657 8.97742 13.5711 9.24264 13.5711C9.50786 13.5711 9.76221 13.4657 9.94975 13.2782L15.5 7.72796C15.6875 7.54042 15.7929 7.28607 15.7929 7.02085C15.7929 6.75563 15.6875 6.50128 15.5 6.31374C15.3125 6.12621 15.0581 6.02085 14.7929 6.02085C14.5277 6.02085 14.2733 6.12621 14.0858 6.31374Z" fill="#39FFFB" />
                </svg>
                <span className={styles.writingTime}>
                  {formatDate(blog.writingTime)}
                </span>
              </span>
            )}
          </div>
          <div className={styles.actionButtons}>
          <button 
                className={`${styles.wishlistButton} ${wishlistStatus ? styles.inWishlist : ''}`}
                onClick={handleWishlistToggle}
              >
                <FaHeart 
                  style={{ 
                    height: '20px', 
                    width: '20px',
                    fill: wishlistStatus ? 'rgb(57, 255, 251)' : 'white'
                  }} 
                />
              </button>
              <button 
                className={styles.shareButton}
                onClick={handleShare}
              >
                <FaShareAlt 
                  style={{ 
                    height: '20px', 
                    width: '20px',
                    fill: 'white'
                  }} 
                />
              </button>
          </div>
          {blog.content.map((contentItem, index) =>
            renderContent(contentItem, index)
          )}
        </div>

        {showSharePopup && (
          <div className={styles.sharePopup}>
            <div className={styles.sharePopupContent}>
              <p>{isRTL ? 'شارك هذا الرابط:' : 'Share this link:'}</p>
              <input type="text" value={window.location.href} readOnly />
              <button onClick={copyToClipboard}>{isRTL ? 'نسخ الرابط' : 'Copy Link'}</button>
              <button onClick={() => setShowSharePopup(false)}>{isRTL ? 'إغلاق' : 'Close'}</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BlogEjsPage;
